import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, Output } from '@angular/core';
import { DataTablePagerComponent as SuperDataTablePagerComponent } from '@swimlane/ngx-datatable';

@Component({
  selector: 'app-datatable-pager',
  template: `
    <ul class="pager" aria-label="Lapozás">
      <li [class.disabled]="!canPrevious()">
        <ion-button class="pager-btn pager-nav" fill="clear"
          href="javascript:void(0)"
          (click)="selectPage(1)">
          <ion-icon name="arrow-undo-outline" slot="icon-only"></ion-icon>
        </ion-button>
      </li>
      <li [class.disabled]="!canPrevious()">
        <ion-button class="pager-btn pager-nav" fill="clear"
          href="javascript:void(0)"
          (click)="prevPage()">
          <ion-icon name="arrow-back-circle-outline" slot="icon-only"></ion-icon>
        </ion-button>
      </li>
      <li
        class="pages"
        *ngFor="let pg of pages"
        [class.active]="pg.number === page">
        <ion-button class="pager-btn pager-page" [fill]="pg.number === page ? 'solid' : 'clear'"
          [color]="pg.number === page ? 'primary' : 'medium'"
          href="javascript:void(0)"
          (click)="selectPage(pg.number)">
          {{pg.text}}
        </ion-button>
      </li>
      <li [class.disabled]="!canNext()">
        <ion-button class="pager-btn pager-nav" fill="clear"
          href="javascript:void(0)"
          (click)="nextPage()">
          <ion-icon name="arrow-forward-circle-outline" slot="icon-only"></ion-icon>
        </ion-button>
      </li>
      <li [class.disabled]="!canNext()">
        <ion-button class="pager-btn pager-nav" fill="clear"
          href="javascript:void(0)"
          (click)="selectPage(totalPages)">
          <ion-icon name="arrow-redo-outline" slot="icon-only"></ion-icon>
        </ion-button>
      </li>
    </ul>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }

    .pager {
      list-style: none;
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      align-items: center;
      gap: 8px;
      margin: 0;
      padding: 8px 10px;
      border-radius: 18px;
      background: rgba(var(--ion-color-light-rgb, 248, 249, 250), 0.92);
      box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.05);
      width: fit-content;
      max-width: 100%;
    }

    .pager li {
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 auto;
    }

    .pager li.disabled {
      opacity: 0.45;
      pointer-events: none;
    }

    .pager-btn {
      margin: 0;
      --border-radius: 14px;
      --box-shadow: none;
      min-width: 40px;
      min-height: 40px;
    }

    .pager-page {
      font-weight: 600;
      --padding-start: 12px;
      --padding-end: 12px;
    }

    .pager-page[fill="clear"] {
      --color: var(--ion-color-medium-shade);
    }

    .pager-nav {
      --color: var(--ion-color-primary);
      --background-hover: rgba(var(--ion-color-primary-rgb), 0.08);
    }

    @media (max-width: 767px) {
      .pager {
        gap: 6px;
        padding: 6px;
      }

      .pager-btn {
        min-width: 36px;
        min-height: 36px;
      }
    }
  `],
  host: {
    class: 'datatable-pager'
  },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DataTablePagerComponent extends SuperDataTablePagerComponent {
  @Input() pagerLeftArrowIcon: string = 'icon-left';
  @Input() pagerRightArrowIcon: string = 'icon-right';
  @Input() pagerPreviousIcon: string = 'icon-prev';
  @Input() pagerNextIcon: string = 'icon-skip';

  @Input()
  set size(val: number) {
    this._size = val;
    this.pages = this.calcPages();
  }

  get size(): number {
    return this._size;
  }

  @Input()
  set count(val: number) {
    this._count = val;
    this.pages = this.calcPages();
  }

  get count(): number {
    return this._count;
  }

  @Input()
  set page(val: number) {
    this._page = val;
    this.pages = this.calcPages();
  }

  get page(): number {
    return this._page;
  }

  get totalPages(): number {
    const count = this.size < 1 ? 1 : Math.ceil(this.count / this.size);
    return Math.max(count || 0, 1);
  }

  @Output() change: EventEmitter<any> = new EventEmitter();

  public _visiblePagesCount: number = 3;

  @Input()
  set visiblePagesCount(val: number) {
    this._visiblePagesCount = val;
    this.pages = this.calcPages();
  }

  get visiblePagesCount(): number {
    return this._visiblePagesCount;
  }

  _count: number = 0;
  _page: number = 1;
  _size: number = 0;
  pages: any;

  canPrevious(): boolean {
    return this.page > 1;
  }

  canNext(): boolean {
    return this.page < this.totalPages;
  }

  prevPage(): void {
    this.selectPage(this.page - 1);
  }

  nextPage(): void {
    this.selectPage(this.page + 1);
  }

  selectPage(page: number): void {
    if (page > 0 && page <= this.totalPages && page !== this.page) {
      this.page = page;

      this.change.emit({
        page
      });
    }
  }

  calcPages(page?: number): any[] {
    const pages = [];
    let startPage = 1;
    let endPage = this.totalPages;
    const maxSize = this.visiblePagesCount;
    const isMaxSized = maxSize < this.totalPages;

    page = page || this.page;

    if (isMaxSized) {
      startPage = page - Math.floor(maxSize / 2);
      endPage = page + Math.floor(maxSize / 2);

      if (startPage < 1) {
        startPage = 1;
        endPage = Math.min(startPage + maxSize - 1, this.totalPages);
      } else if (endPage > this.totalPages) {
        startPage = Math.max(this.totalPages - maxSize + 1, 1);
        endPage = this.totalPages;
      }
    }

    for (let num = startPage; num <= endPage; num++) {
      pages.push({
        number: num,
        text: <string><any>num
      });
    }

    return pages;
  }
}
